// SelectDropdown — field-shaped single select: an input-shell trigger showing
// the selected label, and a detached listbox panel whose selected row is
// brand-tinted and check-marked. Reach for it over the native `Select` when the
// rows need styling (icons, tinted selection) or the panel must escape a
// clipping ancestor. Only the open state lives here — the value is the
// caller's. It renders no label: wrap it in `Field`, exactly like `Select`.
//
// Size note: the ladder follows Button (sm 2.5rem / md 3rem / lg 3.5rem), NOT
// Input/Select (sm 3rem / md 3.5rem). A `md` SelectDropdown is therefore
// shorter than a `md` Input — pair it with `size='sm'` inputs, or step up to
// `lg` to sit level with a default Input.

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from './Dropdown';
import Icon from './Icon';

export type SelectDropdownSize = 'sm' | 'md' | 'lg';

export interface SelectDropdownOption<V extends string = string> {
  value: V;
  label: string;
  /** Kebab-case lucide name, rendered before the label. Omit for a text-only
   *  row — an absent icon collapses entirely, leaving no gap. */
  icon?: string;
  disabled?: boolean;
}

/** Generic over the value union (e.g. a backend status type), so `onChange`
 *  hands back the caller's own type without a cast.
 *
 *  `options` is the ONLY inference site — `value` and `onChange` are wrapped in
 *  `NoInfer` deliberately. The natural call shape passes a `useState` setter to
 *  `onChange`, whose parameter is `SetStateAction<V>` (a union that includes a
 *  function); inferring from it yields a candidate that violates `V extends
 *  string`, and TS then silently falls back to the constraint, collapsing the
 *  caller's union to `string`.
 *
 *  A consequence worth knowing: inline option literals narrow `V` to their own
 *  union, so a caller holding a plain `string` should keep the options in a
 *  const (which widens) or name the type — `<SelectDropdown<string> …>`. */
export interface SelectDropdownProps<V extends string = string> {
  options: SelectDropdownOption<V>[];
  /** Selected value; `null` renders the placeholder. */
  value: NoInfer<V> | null;
  onChange: (next: NoInfer<V>) => void;
  /** id for the trigger button, so a wrapping `Field`'s `htmlFor` can target it. */
  id?: string;
  /** Accessible name for the trigger. Needed when the control is NOT wrapped in
   *  a `Field` (whose label + htmlFor names it) — e.g. a bare `<span>` label. */
  'aria-label'?: string;
  /** Trigger text while nothing is selected. Default t('select_dropdown_placeholder'). */
  placeholder?: string;
  /** Control height: 'sm' 2.5rem, 'md' (default) 3rem, 'lg' 3.5rem. */
  size?: SelectDropdownSize;
  /** Leading icon inside the trigger (kebab-case lucide name). */
  iconStart?: string;
  disabled?: boolean;
  /** Red border + red ring, to pair with `Field`'s error row. */
  error?: boolean;
  /** Panel edge to align with the trigger. Default 'start' (flips in RTL). */
  align?: 'start' | 'end';
  /** Fixed panel width in px. Default: matches the trigger. Wins in both the
   *  in-flow and the portal path. */
  width?: number;
  /** Render the panel in a body portal to escape a clipping ancestor. */
  portal?: boolean;
  /** Portal only — flip above the trigger near the viewport bottom. */
  flip?: boolean;
  /** Cap the option list height in px and scroll past it. Default 320. */
  maxHeight?: number;
  className?: string;
}

const ICON_SIZE: Record<SelectDropdownSize, number> = { sm: 16, md: 18, lg: 20 };

/** Row glyphs sit a step below the trigger's, to match the smaller row text. */
const ROW_ICON_SIZE: Record<SelectDropdownSize, number> = { sm: 14, md: 16, lg: 18 };

/** Typing pauses longer than this start a fresh typeahead buffer. */
const TYPEAHEAD_RESET = 500;

const SelectDropdown = <V extends string = string>({
  options,
  value,
  onChange,
  id,
  'aria-label': ariaLabel,
  placeholder,
  size = 'md',
  iconStart,
  disabled = false,
  error = false,
  align = 'start',
  width,
  portal = false,
  flip = false,
  maxHeight = 320,
  className = '',
}: SelectDropdownProps<V>) => {
  const { t } = useTranslation();
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const optionId = (index: number) => `${uid}-opt-${index}`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  /** Keyboard-highlighted row, pointed at by aria-activedescendant. -1 = none. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const [measured, setMeasured] = useState<number>();
  const typeahead = useRef({ buf: '', at: 0 });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  /** First enabled option from `from`, walking `dir`; -1 when there is none. */
  const step = (from: number, dir: 1 | -1) => {
    for (let i = from; i >= 0 && i < options.length; i += dir) {
      if (!options[i].disabled) return i;
    }
    return -1;
  };
  const firstEnabled = () => step(0, 1);
  const lastEnabled = () => step(options.length - 1, -1);

  // The portal path positions with fixed coords, so it needs a px width. Measure
  // in the event handler rather than a layout effect: this component is the
  // PARENT of Dropdown, and child layout effects run first — a measurement made
  // in ours would land after Dropdown's placement pass, so the panel would paint
  // once at the 200px fallback and snap to width a frame later.
  const measure = () => {
    if (portal) setMeasured(triggerRef.current?.offsetWidth);
  };

  const openPanel = (fallback: number) => {
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : fallback);
    measure();
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
    typeahead.current = { buf: '', at: 0 };
  };

  const pick = (next: V) => {
    onChange(next);
    close();
    triggerRef.current?.focus();
  };

  /** Jump to the first enabled option whose label starts with the buffer. */
  const typeaheadTo = (char: string, from: number) => {
    const now = Date.now();
    const buffer = typeahead.current;
    buffer.buf = now - buffer.at > TYPEAHEAD_RESET ? char : buffer.buf + char;
    buffer.at = now;

    const needle = buffer.buf.toLowerCase();
    // A repeated single character walks to the NEXT match; a longer buffer keeps
    // refining the current one, so it must be allowed to match in place.
    const start = Math.max(from, 0) + (buffer.buf.length > 1 ? 0 : 1);
    for (let n = 0; n < options.length; n += 1) {
      const i = (start + n) % options.length;
      const option = options[i];
      if (!option.disabled && option.label.toLowerCase().startsWith(needle)) {
        setActiveIndex(i);
        return;
      }
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const { key } = e;

    // Dropdown's document-level listener already closes the panel on Escape;
    // close() here too so activeIndex and the typeahead buffer reset in the same
    // tick either way. It is idempotent, so the double call is harmless.
    if (key === 'Escape' || key === 'Tab') {
      if (open) close();
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
      e.preventDefault();
      const forward = key === 'ArrowDown' || key === 'Home';
      if (!open) {
        openPanel(forward ? firstEnabled() : lastEnabled());
        return;
      }
      if (key === 'Home') setActiveIndex(firstEnabled());
      else if (key === 'End') setActiveIndex(lastEnabled());
      else {
        const next = forward ? step(activeIndex + 1, 1) : step(activeIndex - 1, -1);
        if (next >= 0) setActiveIndex(next); // stop at the ends, don't wrap
      }
      return;
    }

    if (key === 'Enter' || key === ' ') {
      // Space must be prevented, or the browser fires a click too and re-toggles.
      e.preventDefault();
      if (!open) {
        openPanel(firstEnabled());
        return;
      }
      const option = options[activeIndex];
      if (option && !option.disabled) pick(option.value);
      return;
    }

    if (key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      if (!open) {
        measure();
        setOpen(true);
      }
      typeaheadTo(key, open ? activeIndex : selectedIndex);
    }
  };

  // getElementById, not querySelector: useId's value contains colons, which are
  // not valid in a CSS selector. Works in both the in-flow and portal paths.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(`${uid}-opt-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, uid]);

  // Dropdown re-places a portaled panel on resize, but with whatever width it
  // was handed — re-measure so it keeps tracking the trigger.
  useEffect(() => {
    if (!open || !portal) return undefined;
    const sync = () => setMeasured(triggerRef.current?.offsetWidth);
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [open, portal]);

  const iconSize = ICON_SIZE[size];
  const triggerClasses = [
    'select-dropdown__trigger',
    `select-dropdown__trigger--${size}`,
    open && 'is-open',
    error && 'is-error',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Dropdown
      open={open}
      onClose={close}
      panelRole='none'
      align={align}
      width={width ?? (portal ? measured : undefined)}
      portal={portal}
      flip={flip}
      className={`select-dropdown${className ? ` ${className}` : ''}`}
      trigger={
        <button
          ref={triggerRef}
          type='button'
          id={id}
          aria-label={ariaLabel}
          className={triggerClasses}
          // combobox, not button: aria-activedescendant is only valid on roles
          // that support it, and this is the APG select-only combobox pattern.
          role='combobox'
          aria-haspopup='listbox'
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-invalid={error || undefined}
          disabled={disabled}
          onClick={() => (open ? close() : openPanel(firstEnabled()))}
          onKeyDown={onKeyDown}
        >
          {iconStart && (
            <Icon
              name={iconStart}
              size={iconSize}
              className='select-dropdown__icon'
              aria-hidden='true'
            />
          )}
          <span className={`select-dropdown__value${selected ? '' : ' is-placeholder'}`}>
            {selected?.label ?? placeholder ?? t('select_dropdown_placeholder')}
          </span>
          <Icon
            name={open ? 'chevron-up' : 'chevron-down'}
            size={iconSize}
            className='select-dropdown__caret'
            aria-hidden='true'
          />
        </button>
      }
    >
      <div
        id={listboxId}
        role='listbox'
        aria-labelledby={id}
        className='select-dropdown__list'
        style={{ maxBlockSize: maxHeight }}
      >
        {options.length === 0 ? (
          <p className='select-dropdown__empty'>{t('select_dropdown_no_options')}</p>
        ) : (
          options.map((option, index) => {
            const isSelected = option.value === value;
            const classes = [
              'select-dropdown__option',
              `select-dropdown__option--${size}`,
              isSelected && 'is-selected',
              index === activeIndex && 'is-active',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              // A div, not a button: focus never leaves the trigger, so rows
              // must not be tab stops.
              <div
                // A null option value (a "none"/unassigned row) would reach
                // React as NO key rather than as the key null, so it needs a
                // stand-in — otherwise the whole list falls back to indices.
                key={option.value ?? '__none'}
                id={optionId(index)}
                role='option'
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                className={classes}
                onClick={() => !option.disabled && pick(option.value)}
                onMouseEnter={() => !option.disabled && setActiveIndex(index)}
              >
                {/* Guard is load-bearing: Icon dev-warns on any name it cannot
                    resolve, so an empty string must never reach it. */}
                {option.icon && (
                  <Icon
                    name={option.icon}
                    size={ROW_ICON_SIZE[size]}
                    className='select-dropdown__option-icon'
                    aria-hidden='true'
                  />
                )}
                <span className='select-dropdown__option-label'>{option.label}</span>
                {isSelected && (
                  <Icon
                    name='check'
                    size={16}
                    className='select-dropdown__check'
                    aria-hidden='true'
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </Dropdown>
  );
};

export default SelectDropdown;
